import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { http } from '../api/http.js';
import { getTeacherToken, setTeacherToken } from '../hooks/useTeacherAuth.js';
import { ArrowRightIcon, PlusIcon, RotateCcwIcon, CloseIcon, LayersIcon } from '../components/Icons.jsx';
import '../styles/teacher.css';

export default function TeacherLogin() {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [rooms, setRooms] = useState(null);
  const [loggedIn, setLoggedIn] = useState(false);
  const [resetTarget, setResetTarget] = useState(null); // room object
  const [removePlayers, setRemovePlayers] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);

  const loadRooms = () => {
    http
      .get('/api/rooms')
      .then((res) => {
        setRooms(res.rooms);
        setLoggedIn(true);
      })
      .catch(() => setLoggedIn(false));
  };

  useEffect(() => {
    if (getTeacherToken()) loadRooms();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const res = await http.post('/api/teacher/login', { code });
      setTeacherToken(res.token);
      loadRooms();
    } catch {
      setError('รหัสไม่ถูกต้อง');
    } finally {
      setBusy(false);
    }
  };

  const createRoom = async () => {
    const res = await http.post('/api/rooms', {});
    navigate(`/teacher/control/${res.id}`);
  };

  const openReset = (room) => {
    setResetTarget(room);
    setRemovePlayers(false);
  };

  const doReset = async () => {
    if (!resetTarget) return;
    setResetBusy(true);
    try {
      await http.post(`/api/rooms/${resetTarget.id}/reset`, { removePlayers });
      setResetTarget(null);
      loadRooms();
    } catch {
      setError('รีเซ็ตห้องไม่สำเร็จ');
    } finally {
      setResetBusy(false);
    }
  };

  if (loggedIn) {
    return (
      <div className="page">
        <div className="page-narrow stack">
          <div className="teacher-brand">
            <span className="teacher-brand-mark">LC</span>
            <span className="teacher-brand-name">ห้องเรียนสด</span>
          </div>
          <div className="card stack">
            <div className="row-between">
              <div>
                <h1>ห้องเรียนของฉัน</h1>
                <p className="text-dim">เลือกห้องเพื่อเริ่มบทเรียนหรือสร้างห้องใหม่</p>
              </div>
              <div className="row" style={{ gap: '8px' }}>
                <button className="btn btn-ghost" onClick={() => navigate('/teacher/history')}>
                  <LayersIcon size={16} />
                  <span>ประวัติการเข้าเรียน</span>
                </button>
                <button className="btn btn-primary" onClick={createRoom}>
                  <PlusIcon size={16} />
                  <span>สร้างห้องใหม่</span>
                </button>
              </div>
            </div>

            {rooms?.length ? (
              <ul className="teacher-room-list">
                {rooms.map((r) => (
                  <li key={r.id} className="row-between teacher-room-item">
                    <div className="row">
                      <span className="teacher-room-code">{r.code}</span>
                      <span className={`status-pill ${r.status === 'live' ? 'is-online' : 'is-offline'}`}>
                        {r.status === 'live' ? 'กำลังเรียน' : r.status === 'ended' ? 'จบแล้ว' : 'พร้อมเริ่ม'}
                      </span>
                    </div>
                    <div className="row">
                      <button
                        className="btn btn-ghost btn-sm teacher-room-reset"
                        onClick={() => openReset(r)}
                        title="เริ่มคาบใหม่ — ประวัติของคาบเดิมถูกเก็บไว้"
                      >
                        <RotateCcwIcon size={14} />
                        <span>เริ่มคาบใหม่</span>
                      </button>
                      {/* Two screens, two links. The control panel is the one
                          to start from — it can open the projector itself —
                          but the projector link has to stand on its own for
                          the machine at the front of the room. */}
                      <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/teacher/host/${r.id}`)}>
                        <span>ฉายสไลด์</span>
                      </button>
                      <button className="btn btn-primary btn-sm" onClick={() => navigate(`/teacher/control/${r.id}`)}>
                        <span>แผงควบคุม</span>
                        <ArrowRightIcon size={14} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-center stack" style={{ padding: 'var(--space-4) 0' }}>
                <p className="text-dim">ยังไม่มีห้องเรียนในระบบ</p>
              </div>
            )}
          </div>
        </div>

        {resetTarget && (
          <div className="teacher-modal-overlay" onClick={() => !resetBusy && setResetTarget(null)}>
            <div className="teacher-modal card stack" onClick={(e) => e.stopPropagation()}>
              <div className="row-between">
                <h2>เริ่มคาบใหม่ในห้อง {resetTarget.code}</h2>
                <button className="btn-close" onClick={() => setResetTarget(null)} aria-label="ปิด">
                  <CloseIcon size={18} />
                </button>
              </div>
              <p className="text-dim">
                ห้องจะกลับไปหน้ารอเริ่ม และคะแนนบนจอเริ่มนับใหม่ ·
                <strong> ประวัติของคาบเดิมถูกเก็บไว้</strong> ดูย้อนหลังได้ที่หน้าประวัติการเข้าเรียน
              </p>
              <label className="teacher-modal-check">
                <input
                  type="checkbox"
                  checked={removePlayers}
                  onChange={(e) => setRemovePlayers(e.target.checked)}
                />
                <span>เอาผู้เรียนออกจากห้องด้วย (ทุกคนต้องเข้าใหม่)</span>
              </label>
              <div className="row" style={{ justifyContent: 'flex-end', gap: '8px' }}>
                <button className="btn btn-ghost" onClick={() => setResetTarget(null)} disabled={resetBusy}>
                  ยกเลิก
                </button>
                <button className="btn btn-primary" onClick={doReset} disabled={resetBusy}>
                  {resetBusy ? 'กำลังเริ่มคาบใหม่...' : 'เริ่มคาบใหม่'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-narrow stack">
        <div className="teacher-brand">
          <span className="teacher-brand-mark">LC</span>
          <span className="teacher-brand-name">ห้องเรียนสด</span>
        </div>
      <form className="card stack" onSubmit={submit}>
        <div>
          <h1>เข้าสู่ระบบสำหรับผู้สอน</h1>
          <p className="text-dim">กรอกรหัส 4 หลักเพื่อจัดการห้องและคำถาม</p>
        </div>

        <div className="field">
          <label htmlFor="code">รหัส</label>
          <input
            id="code"
            className="teacher-code-input"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
            placeholder="••••"
            inputMode="numeric"
            pattern="[0-9]*"
            type="password"
            autoComplete="one-time-code"
            maxLength={4}
            autoFocus
          />
        </div>

        {error && <p className="field-error">{error}</p>}

        <button className="btn btn-primary btn-block" type="submit" disabled={busy || code.length !== 4}>
          <span>{busy ? 'กำลังเข้าสู่ระบบ...' : 'เข้าสู่ระบบ'}</span>
          {!busy && <ArrowRightIcon size={16} />}
        </button>
      </form>
      </div>
    </div>
  );
}
